import type {Operator, Query, SearchBarColumn, SearchBarColumns} from './types.js'

// The pure logic of the search field and its chips, kept out of the components so it can be tested without a DOM

export interface OperatorInfo {
    label: string
    /** mdi icon class, without one the operator itself is shown */
    icon?: string
}

export const operators: Record<Operator, OperatorInfo> = {
    '=': {label: 'Bevat', icon: 'mdi-equal'},
    '==': {label: 'Is gelijk aan'},
    '!=': {label: 'Niet gelijk aan', icon: 'mdi-not-equal-variant'},
    '>': {label: 'Groter dan', icon: 'mdi-greater-than'},
    '<': {label: 'Kleiner dan', icon: 'mdi-less-than'}
}

const allOperators = Object.keys(operators) as Operator[]

// filter.js only handles these on the whole row, the others would filter out every row
const rowOperators: Operator[] = ['=', '!=']

export const defaultOperator: Operator = '='

/** The operators that can be used with the column, or on the whole row without one */
export function getOperators(column: SearchBarColumn | null): Operator[] {
    return column ? allOperators : rowOperators
}

/** The operator to keep when the column changes: one the new choice doesn't support falls back to the default */
export function fitOperator(operator: Operator, column: SearchBarColumn | null): Operator {
    return getOperators(column).includes(operator) ? operator : defaultOperator
}

/**
 * The columns that can be searched in, sorted by title. A column needs a title (shown in the list and the chip) and a
 * key (searched in): one without a key would search the whole row while its chip says it searches the column.
 */
export function getSearchColumns(columns: SearchBarColumns | undefined): SearchBarColumn[] {
    const list = Array.isArray(columns) ? columns : Object.values(columns || {})

    return list
        .filter((column) => column && typeof column.title === 'string' && column.title && column.key)
        .sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * The query for the search field's content, or null when there is nothing to search: a column with an empty text is a
 * query (the column is empty, see filter.js), free text needs more than whitespace
 */
export function buildQuery(column: SearchBarColumn | null, operator: Operator, text: string): Query | null {
    if (!column && !text.trim()) {
        return null
    }

    return {
        key: column ? column.key : '',
        title: column ? column.title : '',
        operator: fitOperator(operator, column),
        text
    }
}

/** New arrays, the caller's (often a store's state) is never changed */
export function addQuery(queries: Query[] | undefined, query: Query): Query[] {
    return [...(queries || []), query]
}

export function removeQuery(queries: Query[] | undefined, index: number): Query[] {
    return (queries || []).filter((_query, i) => i !== index)
}

/** A readable description, for the chip's accessible name: 'Naam bevat jan' */
export function describeQuery(query: Query) {
    const operator = operators[query.operator]?.label.toLowerCase() || query.operator
    const text = query.text ? ` ${query.text}` : ' leeg'

    return query.title ? `${query.title} ${operator}${text}` : `Alles ${operator}${text}`
}

/**
 * Typeahead in a list: the first item from the active one (not including it, so typing the same letter again moves on)
 * whose label starts with the typed text, wrapping around. -1 when none matches.
 */
export function findTypeahead(labels: string[], typed: string, activeIndex: number) {
    const search = typed.toLowerCase()
    if (!search) {
        return -1
    }

    // While the typed text grows ('na', 'naa') the active item itself may still match, so start at it
    const start = search.length > 1 ? Math.max(activeIndex, 0) : activeIndex + 1

    for (let i = 0; i < labels.length; i++) {
        const index = (start + i) % labels.length
        if (labels[index].toLowerCase().startsWith(search)) {
            return index
        }
    }

    return -1
}

/** The next index for an arrow/Home/End key in a list of length items, null for other keys */
export function moveIndex(key: string, index: number, length: number): number | null {
    if (!length) {
        return null
    }

    switch (key) {
        case 'ArrowDown':
            return index < length - 1 ? index + 1 : 0
        case 'ArrowUp':
            return index > 0 ? index - 1 : length - 1
        case 'Home':
            return 0
        case 'End':
            return length - 1
        default:
            return null
    }
}
